'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Search, Bell, X, Users, Layers, Package, LogOut } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/lib/AuthContext';
import { searchUsersFromDB, PublicUserProfile } from '@/lib/userStore';
import AppImage from '@/components/ui/AppImage';
import CategoryIcon from '@/components/ui/CategoryIcon';

type SearchType = 'clips' | 'scenepacks' | 'users';

interface TopSearchBarProps {
  onSearch?: (query: string) => void;
}

const searchTypes: { key: SearchType; label: string; icon: React.ElementType; placeholder: string }[] = [
  { key: 'clips', label: 'Clip Bundles', icon: Package, placeholder: 'Search clip bundles...' },
  { key: 'scenepacks', label: 'Scenepacks', icon: Layers, placeholder: 'Search scenepacks...' },
  { key: 'users', label: 'Users', icon: Users, placeholder: 'Search users...' },
];

export default function TopSearchBar({ onSearch }: TopSearchBarProps) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [searchType, setSearchType] = useState<SearchType>('clips');
  const [userResults, setUserResults] = useState<PublicUserProfile[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const typeMenuRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { user, isLoading, logout } = useAuth();

  useEffect(() => {
    if (searchType !== 'users' || query.trim().length < 1) {
      setUserResults([]);
      setShowDropdown(false);
      return;
    }
    let cancelled = false;
    searchUsersFromDB(query).then((results) => {
      if (!cancelled) {
        const top5 = results.slice(0, 5);
        setUserResults(top5);
        setShowDropdown(top5.length > 0);
      }
    });
    return () => { cancelled = true; };
  }, [query, searchType]);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
      if (typeMenuRef.current && !typeMenuRef.current.contains(e.target as Node)) {
        setShowTypeMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    onSearch?.(e.target.value);
  };

  const clearSearch = () => {
    setQuery('');
    onSearch?.('');
    setShowDropdown(false);
  };

  const getSearchHref = (q: string) => {
    if (searchType === 'users') return `/users?q=${encodeURIComponent(q)}`;
    if (searchType === 'scenepacks') return `/discover?type=scenepacks&q=${encodeURIComponent(q)}`;
    return `/discover?type=clips&q=${encodeURIComponent(q)}`;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && query.trim()) {
      setShowDropdown(false);
      router.push(getSearchHref(query.trim()));
    }
    if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleSearchClick = () => {
    setShowDropdown(false);
    if (query.trim()) {
      router.push(getSearchHref(query.trim()));
    } else {
      if (searchType === 'users') router.push('/users');
      else router.push('/discover');
    }
  };

  const activeType = searchTypes.find((t) => t.key === searchType)!;
  const ActiveIcon = activeType.icon;

  return (
    <header
      className="sticky top-0 z-40 bottom-nav-blur"
      style={{ background: 'rgba(10, 10, 15, 0.92)', borderBottom: '1px solid var(--border)' }}
    >
      <div className="flex items-center gap-3 px-4 py-3 max-w-screen-2xl mx-auto">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2 flex-shrink-0">
          <AppLogo size={32} />
          <span className="font-bold text-lg tracking-tight hidden sm:block" style={{ color: 'var(--foreground)' }}>
            Clip<span className="text-gradient-primary">Hub</span>
          </span>
        </Link>

        {/* Search */}
        <div className="flex-1 relative" ref={dropdownRef}>
          <div className="flex items-center gap-0" style={{ background: 'var(--muted)', borderRadius: '0.75rem', border: '1px solid var(--border)' }}>
            {/* Search type selector */}
            <div className="relative flex-shrink-0" ref={typeMenuRef}>
              <button
                onClick={() => setShowTypeMenu((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold rounded-l-xl transition-colors hover:opacity-80 border-r"
                style={{
                  color: 'var(--primary)',
                  borderColor: 'var(--border)',
                  background: 'rgba(108, 99, 255, 0.08)',
                  borderRadius: '0.75rem 0 0 0.75rem',
                }}
              >
                <CategoryIcon category={activeType.key === 'users' ? 'users' : activeType.key === 'scenepacks' ? 'scenepacks' : 'clip bundles'} size={13} />
                <span className="hidden sm:block">{activeType.label}</span>
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ opacity: 0.6 }}>
                  <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {showTypeMenu && (
                <div
                  className="absolute top-full left-0 mt-1 rounded-xl overflow-hidden shadow-2xl z-50 min-w-[140px]"
                  style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
                >
                  {searchTypes.map((type) => {
                    const TypeIcon = type.icon;
                    return (
                      <button
                        key={type.key}
                        onClick={() => { setSearchType(type.key); setShowTypeMenu(false); setQuery(''); onSearch?.(''); }}
                        className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm font-medium transition-colors hover:opacity-80 text-left"
                        style={{
                          color: searchType === type.key ? 'var(--primary)' : 'var(--foreground)',
                          background: searchType === type.key ? 'rgba(108, 99, 255, 0.1)' : 'transparent',
                          borderBottom: '1px solid var(--border)',
                        }}
                      >
                        <CategoryIcon category={type.key === 'users' ? 'users' : type.key === 'scenepacks' ? 'scenepacks' : 'clip bundles'} size={14} />
                        {type.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Input */}
            <div className="relative flex-1">
              <button
                onClick={handleSearchClick}
                className="absolute left-3 top-1/2 -translate-y-1/2 transition-colors z-10"
                style={{ color: focused ? 'var(--primary)' : 'var(--muted-foreground)' }}
                aria-label="Search"
              >
                <Search size={15} />
              </button>
              <input
                type="text"
                value={query}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={() => { setFocused(true); if (userResults.length > 0) setShowDropdown(true); }}
                onBlur={() => setFocused(false)}
                placeholder={activeType.placeholder}
                className="w-full pl-9 pr-9 py-2.5 text-sm bg-transparent outline-none"
                style={{ color: 'var(--foreground)' }}
              />
              {query && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-opacity hover:opacity-80 z-10"
                  style={{ color: 'var(--muted-foreground)' }}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* User search dropdown (only for users type) */}
          {showDropdown && searchType === 'users' && (
            <div
              className="absolute top-full left-0 right-0 mt-1 rounded-xl overflow-hidden shadow-2xl z-50"
              style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
            >
              <div className="px-3 py-2 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)' }}>
                <span className="text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>Users</span>
                <Link
                  href={`/users?q=${encodeURIComponent(query)}`}
                  className="text-xs font-semibold"
                  style={{ color: 'var(--primary)' }}
                  onClick={() => setShowDropdown(false)}
                >
                  See all
                </Link>
              </div>
              {userResults.map((u) => (
                <Link
                  key={u.id}
                  href={`/creator/${encodeURIComponent(u.username)}`}
                  onClick={() => { setShowDropdown(false); setQuery(''); }}
                  className="flex items-center gap-3 px-3 py-2.5 transition-colors hover:opacity-80"
                  style={{ borderBottom: '1px solid var(--border)' }}
                >
                  <div className="relative w-8 h-8 rounded-lg overflow-hidden flex-shrink-0" style={{ background: 'var(--muted)' }}>
                    {u.avatarUrl ? (
                      <AppImage src={u.avatarUrl} alt={`${u.displayName} avatar`} fill className="object-cover" sizes="32px" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-sm font-bold" style={{ color: 'var(--muted-foreground)' }}>
                        {u.displayName?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: 'var(--foreground)' }}>{u.displayName}</p>
                    <p className="text-xs truncate" style={{ color: 'var(--muted-foreground)' }}>@{u.username}</p>
                  </div>
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{u.followerCount} followers</span>
                </Link>
              ))}
              <Link
                href={`/users?q=${encodeURIComponent(query)}`}
                onClick={() => setShowDropdown(false)}
                className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium transition-colors hover:opacity-80"
                style={{ color: 'var(--primary)' }}
              >
                <Users size={14} />
                Search all users for &quot;{query}&quot;
              </Link>
            </div>
          )}
        </div>

        {/* Right side: Notifications + Auth */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {!isLoading && user && (
            <Link
              href="/notifications"
              className="relative w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-150 hover:scale-105"
              style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}
              aria-label="Notifications"
            >
              <Bell size={16} />
            </Link>
          )}

          {!isLoading && (
            user ? (
              <div className="flex items-center gap-2">
                <Link
                  href="/profile"
                  className="flex items-center gap-2 px-3 py-2 rounded-xl transition-all duration-150 hover:opacity-80"
                  style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
                >
                  {user.profile?.avatarUrl ? (
                    <div className="relative w-6 h-6 rounded-lg overflow-hidden flex-shrink-0">
                      <AppImage src={user.profile.avatarUrl} alt="Your avatar" fill className="object-cover" sizes="24px" />
                    </div>
                  ) : (
                    <div
                      className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ background: 'var(--primary)', color: 'white' }}
                    >
                      {user.profile?.displayName?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <span className="text-xs font-semibold hidden sm:block" style={{ color: 'var(--foreground)' }}>
                    {user.profile?.displayName || 'Profile'}
                  </span>
                </Link>
                <button
                  onClick={async () => { await logout(); router.push('/sign-up-login-screen'); }}
                  className="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-150 hover:opacity-80"
                  style={{ background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}
                  aria-label="Sign out"
                  title="Sign out"
                >
                  <LogOut size={16} />
                </button>
              </div>
            ) : (
              <Link
                href="/sign-up-login-screen"
                className="btn-primary px-4 py-2 text-xs font-semibold"
              >
                Sign In
              </Link>
            )
          )}
        </div>
      </div>
    </header>
  );
}